import { createSelector } from '@reduxjs/toolkit';
import { flatten, values } from 'lodash';
import { IWebFormContent } from '@qodly/shared';
import { ITab } from '@qodly/shared';
import { selectRoot } from '.';
import { AppState } from '../modules';
import { selectAppState } from './common';

/**
 * Returns the list of declared datasources in the current tab.
 * @param tabPath the current tab path.
 * @returns datasources or an empty array if not found.
 */
export const selectDatasourcesByPath = (tabPath: string) =>
  createSelector(selectRoot, (root) => {
    const tab = root.tabs.find(
      ({ path }) => path === tabPath,
    ) as ITab<IWebFormContent>;
    return tab?.content?.metadata?.datasources || [];
  });

/**
 * Returns the declaration object for the datasource.
 * @param id datasource id.
 * @param namespace datasource namespace.
 * @param tabPath the current tab path.
 * @returns datasource or undefined if not found.
 */
export const selectDatasource = (
  id: string,
  namespace: string,
  tabPath: string,
) =>
  createSelector(selectAppState, (state) => {
    if (!namespace) {
      const tab = state.root.tabs.find((tab) => tab.path === tabPath);
      if (!tab) return undefined;
      const declaration = (
        tab.content as IWebFormContent
      ).metadata?.datasources?.find((src) => src.id === id);
      return declaration;
    } else {
      const sharedNamespace = state.sharedDatasources.sources[namespace];

      if (!sharedNamespace) return undefined;

      return sharedNamespace?.find((src) => src.id === id);
    }
  });

/**
 * Returns the shared datasources namespaces.
 */
export const selectSharedDatasources = createSelector(
  (state: AppState) => state,
  (state) => {
    return state.sharedDatasources?.sources || {};
  },
);

export const selectSharedDatasourcesState = createSelector(
  (state: AppState) => state,
  (state) => {
    return state.sharedDatasources?.state;
  },
);

export type TDatasourcesFilter = (
  | datasources.ICreateDataSource['type']
  | datasources.ICreateDataSourceScalar['dataType']
  | 'objectFromArray'
)[];

export type TDatasourcesNotIn = {
  id: string;
  namespace?: string;
}[];

/**
 * Returns the list of declared & shared datasources in the current tab.
 * @param tabPath the current tab path.
 * @returns datasource or an empty array if not found.
 */
export const selectAllDatasourcesByPath = (
  tabPath: string,
  exclude?: 'private' | 'shared' | 'user',
  filter?: TDatasourcesFilter | null,
  notIn?: TDatasourcesNotIn,
) =>
  createSelector(
    selectDatasourcesByPath(tabPath),
    selectSharedDatasources,
    (privateDatasources, sharedDatasources) => {
      const sharedSources = flatten(values(sharedDatasources));

      const result = [
        ...(exclude !== 'private' ? privateDatasources : []),
        ...(exclude !== 'shared' ? sharedSources : []),
      ];

      return filter
        ? result.filter(
            (item) =>
              (!notIn ||
                notIn.every(
                  ({ id, namespace = '' }) =>
                    item.id !== id || (item.namespace || '') !== namespace,
                )) &&
              (filter.includes(item.type) ||
                (item.dataType &&
                  (filter.includes(
                    item.dataType as datasources.ICreateDataSourceScalar['dataType'],
                  ) ||
                    (filter.includes('objectFromArray') &&
                      item.dataType === 'object' &&
                      (item as datasources.ICreateDataSourceObject).from)))),
          )
        : result;
    },
  );
